import { z } from 'zod'

import { toLocalInput } from '@ems/contracts/timezones'

import { api } from '#admin/lib/api-client.ts'
import { utcInstant } from '#admin/lib/utc-instant.ts'

/**
 * The booth competition, as the organizer sees it.
 *
 * The organizer's leaderboard is `/admin/leaderboard`, not `/leaderboard`, and the difference
 * is the whole of Rule A: the public one refuses with a `conflict` until results are published,
 * and an organizer needs to see the standings while deciding whether to publish them.
 */
export const settingsSchema = z.object({
  isOpen: z.boolean(),
  opensAt: z.string().nullable(),
  closesAt: z.string().nullable(),
  resultsPublic: z.boolean(),
  allowRevote: z.boolean(),
  maxVotesPerVisitor: z.number().int(),
})

export const leaderboardSchema = z.object({
  items: z.array(
    z.object({
      entryId: z.string(),
      boothCode: z.string(),
      displayName: z.string(),
      votes: z.number().int(),
    }),
  ),
})

export type VotingSettings = z.infer<typeof settingsSchema>
export type Leaderboard = z.infer<typeof leaderboardSchema>

export function fetchSettings(signal?: AbortSignal): Promise<VotingSettings> {
  return api.get('/v1/voting/settings', { schema: settingsSchema, ...(signal ? { signal } : {}) })
}

export function fetchLeaderboard(signal?: AbortSignal): Promise<Leaderboard> {
  return api.get('/v1/voting/admin/leaderboard', {
    schema: leaderboardSchema,
    ...(signal ? { signal } : {}),
  })
}

/**
 * Is the competition actually accepting votes right now?
 *
 * **Both the switch and the window**, evaluated the same way the server does. Restating the
 * rule in the client is a real risk — two implementations of "is it open" is two answers — but
 * the alternative is worse: a console that showed "Open" from `isOpen` alone would say the
 * competition is running for the fortnight between the switch being flipped and the window
 * beginning, and the organizer would spend that fortnight wondering why nobody is voting.
 *
 * The server refuses the vote either way. This only decides what the badge says.
 */
export function acceptingVotes(settings: VotingSettings, now = new Date()): boolean {
  if (!settings.isOpen) return false
  const at = now.getTime()
  if (settings.opensAt !== null && at < Date.parse(settings.opensAt)) return false
  if (settings.closesAt !== null && at > Date.parse(settings.closesAt)) return false
  return true
}

/** Why it is not accepting votes, for a console that should not make an operator guess. */
export function closedBecause(settings: VotingSettings, now = new Date()): string | null {
  if (acceptingVotes(settings, now)) return null
  if (!settings.isOpen) return 'Voting is switched off.'

  const at = now.getTime()
  if (settings.opensAt !== null && at < Date.parse(settings.opensAt)) {
    return `Voting opens ${new Date(settings.opensAt).toLocaleString()}.`
  }
  return `Voting closed ${settings.closesAt === null ? '' : new Date(settings.closesAt).toLocaleString()}.`
}

// --- running the competition ------------------------------------------------------------------

/**
 * Replace the settings, whole. The server **replaces** rather than merges, so every field is sent:
 * a partial save would make "close the voting" and "clear the closing time" the same request.
 */
export function saveVotingSettings(settings: VotingSettings): Promise<VotingSettings> {
  return api.put('/v1/voting/admin/settings', { schema: settingsSchema, body: settings })
}

const entryListSchema = z.object({
  items: z.array(z.object({ id: z.string(), boothCode: z.string(), displayName: z.string() })),
})

/**
 * Enter every confirmed stand that is not in the competition yet. Entries already there stay as
 * they are, so the sync is safe to press again. The answer is every entry after the sync.
 */
export function syncVotingEntries(): Promise<z.infer<typeof entryListSchema>> {
  return api.post('/v1/voting/admin/entries/sync', { schema: entryListSchema, body: {} })
}

/** What the settings dialog edits. Times are `datetime-local` values in the edition's zone. */
export type VotingSettingsDraft = {
  isOpen: boolean
  opensAt: string
  closesAt: string
  resultsPublic: boolean
  allowRevote: boolean
  maxVotesPerVisitor: string
}

export function settingsDraftOf(settings: VotingSettings, zone: string): VotingSettingsDraft {
  return {
    isOpen: settings.isOpen,
    opensAt: settings.opensAt === null ? '' : toLocalInput(settings.opensAt, zone),
    closesAt: settings.closesAt === null ? '' : toLocalInput(settings.closesAt, zone),
    resultsPublic: settings.resultsPublic,
    allowRevote: settings.allowRevote,
    maxVotesPerVisitor: String(settings.maxVotesPerVisitor),
  }
}

/** The server's limit on votes per visitor. */
export const MAX_VOTES_LIMIT = 50

/** What is wrong with a draft, by field. Empty when the server would accept it. */
export function settingsIssues(draft: VotingSettingsDraft): Record<string, string> {
  const issues: Record<string, string> = {}
  const max = draft.maxVotesPerVisitor.trim()
  if (!/^\d+$/.test(max) || Number(max) < 1 || Number(max) > MAX_VOTES_LIMIT) {
    issues['maxVotesPerVisitor'] = `Enter a whole number from 1 to ${MAX_VOTES_LIMIT}.`
  }
  // `datetime-local` values sort as strings, and both are in the same zone.
  if (draft.opensAt !== '' && draft.closesAt !== '' && draft.closesAt <= draft.opensAt) {
    issues['closesAt'] = 'Voting must close after it opens.'
  }
  return issues
}

/**
 * The wire body: every field, with an empty time sent as `null` (no limit). Times go as UTC: the
 * settings endpoint refuses an instant written with an offset.
 */
export function settingsBodyOf(draft: VotingSettingsDraft, zone: string): VotingSettings {
  const instant = (value: string) => (value === '' ? null : utcInstant(value, zone))
  return {
    isOpen: draft.isOpen,
    opensAt: instant(draft.opensAt),
    closesAt: instant(draft.closesAt),
    resultsPublic: draft.resultsPublic,
    allowRevote: draft.allowRevote,
    maxVotesPerVisitor: Number(draft.maxVotesPerVisitor.trim()),
  }
}
